import axios from 'axios';
import { isTokenExpired } from './Token';

/** URL de produção da API (prefixo /api no mesmo host — nginx faz proxy para :4100). */
const PRODUCTION_API_URL = '/api/';

/** URL do backend local (sem prefixo /api; Kestrel expõe as rotas na raiz). */
const DEFAULT_LOCAL_API_URL = 'http://localhost:4100/';

/**
 * Obtém a URL base da API conforme o ambiente:
 * - Se REACT_APP_API_URL estiver definida (build ou .env), usa ela.
 * - Se o acesso for por localhost/127.0.0.1, usa o backend local.
 * - Caso contrário, usa produção.
 */
export const getApiBaseURL = () => {
  if (typeof process !== 'undefined' && process.env?.REACT_APP_API_URL) {
    const url = String(process.env.REACT_APP_API_URL).trim();
    if (url) return url.endsWith('/') ? url : `${url}/`;
  }
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') return DEFAULT_LOCAL_API_URL;
  }
  return PRODUCTION_API_URL;
};

export const API_BASE_URL = getApiBaseURL();

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000,
});

// Adiciona automaticamente Bearer token + contexto de empresa/filial ativa.
api.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    const empresaId = sessionStorage.getItem('empresaId');
    const filialId = sessionStorage.getItem('filialId');

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (empresaId) {
      config.headers['X-Empresa-Id'] = empresaId;
    }
    if (filialId) {
      config.headers['X-Filial-Id'] = filialId;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Trata 401: se o token realmente expirou (>18h), limpa sessão e volta ao login.
// Se o token ainda é válido, é erro de permissão — não deslogar.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      if (isTokenExpired()) {
        sessionStorage.clear();
        localStorage.removeItem('token');
        localStorage.removeItem('tokendatahora');
        window.location.href = '/login?sessao=expirada';
        return Promise.reject(new Error('Sessão expirada. Por favor, faça login novamente.'));
      }
    }
    return Promise.reject(error);
  }
);

function toCamelKey(key) {
  // Trata siglas curtas em caixa alta (ex.: "UF") como um único token minúsculo.
  if (key.length <= 4 && key === key.toUpperCase()) return key.toLowerCase();
  return key.charAt(0).toLowerCase() + key.slice(1);
}

/**
 * Normaliza recursivamente as chaves de um objeto/array vindo da API (o backend
 * .NET serializa em PascalCase) para camelCase, permitindo consumo consistente
 * no frontend independente da convenção usada no serializer.
 */
export function normalize(data) {
  if (Array.isArray(data)) return data.map(normalize);
  if (data && typeof data === 'object' && !(data instanceof Date)) {
    return Object.keys(data).reduce((acc, key) => {
      acc[toCamelKey(key)] = normalize(data[key]);
      return acc;
    }, {});
  }
  return data;
}

export default api;
